/* eslint-disable no-unused-vars */
import { opsMissionTypeEnum, parameterStruct, property } from "./dataStruct";
import * as userReadCache from "../../services/database/user-read-cache";

/**
 * Write-through cache for `opsctrl_flightopscontroller_param` reads, live only
 * inside `withParamCache()`.
 *
 * Why it exists: one calculation chain run issued 670 `/query` requests
 * carrying 138 distinct questions — 79% repeats. The chain reads the same
 * scope over and over because each step asks for one parameter at a time, and
 * on the web every read is an HTTP round trip.
 *
 * Why it is a RUN scope and not a long-lived cache with a TTL: the only writer
 * this cache can follow is us. A peer's sync write, a backfill or a migration
 * changes the table without going through `addParameter`, and a cache that
 * outlives the run would serve the old value afterwards — a plausible wrong
 * number on a fuel or mass-and-balance surface, which is the one failure
 * direction an EFB must not have. Inside one run a peer's parameter change
 * clears the map: the sync layer calls `clear` (web: the REMOTE frame, native:
 * after the row is stored).
 *
 * Fail-safe by construction: OUTSIDE `withParamCache()` every read goes to the
 * database exactly as before. An entry point nobody wrapped is slow, never
 * wrong.
 *
 * A read and a write of one scope can overlap. The overlap rule below decides
 * whether a read may keep its answer; `param-cache.spec.ts` has one case per
 * order of events. Add a case there before you change the rule.
 *
 * Coherence rules, and all three are load-bearing:
 *   - every write through `addParameter` upserts here (read-after-write),
 *   - `dropScope` follows `deleteAllParameters` (reconcile-to-empty),
 *   - `clear` follows any delete whose scope this map cannot express.
 * A new write path that bypasses all three makes this cache lie. There is no
 * check that catches that, so keep the param writes going through
 * `functions.ts`.
 */

/** The row key a param is addressed by, minus the paramTypeId. */
function scopeKey(hash: string, tableSuffix: string, opsMissionType: opsMissionTypeEnum): string {
  return hash + "\u0000" + tableSuffix + "\u0000" + String(opsMissionType);
}

let scopes: Map<string, parameterStruct[]> | null = null;
let depth = 0;
/**
 * The overlap rule. A read may store its answer only when no write of its
 * scope overlapped it, and nothing was cleared meanwhile:
 *   - `writes` counts, per scope, every write that was issued and every
 *     immediate write that settled;
 *   - `open` counts, per scope, the immediate writes that are sent and not
 *     settled. The database may answer a read before such a write lands;
 *   - `clears` counts every clear and every dropped scope.
 * A read takes a token before it flushes. The flush lands every write queued
 * up to then, so an answer whose token still matches holds all of them.
 * Anything else makes the read skip its store, and the next read asks again.
 */
const writes = new Map<string, number>();
const open = new Map<string, number>();
let clears = 0;

export interface ReadToken {
  clears: number;
  writes: number;
  quiet: boolean;
}

/** Take this before the flush of a scope read, and pass it to `setScope`. */
export function readToken(hash: string, tableSuffix: string, opsMissionType: opsMissionTypeEnum): ReadToken {
  const key = scopeKey(hash, tableSuffix, opsMissionType);
  return { clears, writes: writes.get(key) ?? 0, quiet: !open.has(key) };
}

/** An immediate write of this scope was sent. Pair it with `writeSettled`. */
export function writeOpened(hash: string, tableSuffix: string, opsMissionType: opsMissionTypeEnum): void {
  const key = scopeKey(hash, tableSuffix, opsMissionType);
  open.set(key, (open.get(key) ?? 0) + 1);
}

/** That write landed or failed. */
export function writeSettled(hash: string, tableSuffix: string, opsMissionType: opsMissionTypeEnum): void {
  const key = scopeKey(hash, tableSuffix, opsMissionType);
  const left = (open.get(key) ?? 1) - 1;
  if (left > 0) open.set(key, left);
  else open.delete(key);
  writes.set(key, (writes.get(key) ?? 0) + 1);
}

/**
 * Run `fn` with the param cache on. Nesting shares one map — the outermost
 * call owns its lifetime — so a wrapped chain step inside a wrapped recycle
 * costs nothing extra and stays coherent.
 *
 * Two runs that overlap in time share the map as well. That is safe for the
 * same reason a single run is: every write goes through the same write-through
 * door, so whichever run wrote last is what both read.
 */
export async function withParamCache<T>(fn: () => Promise<T>): Promise<T> {
  if (depth === 0) {
    scopes = new Map();
    // No token of an earlier run may match in this one. `open` stays: a write
    // sent before this run can still land in it.
    clears++;
    writes.clear();
    // The efb_user read cache shares this lifetime exactly — it is the same
    // argument about the same run, applied to the rest of the chain's reads.
    userReadCache.beginRun();
  }
  depth++;
  try {
    return await fn();
  } finally {
    depth--;
    if (depth === 0) {
      scopes = null;
      userReadCache.endRun();
    }
  }
}

/** True while a `withParamCache()` run is in progress. */
export function isActive(): boolean {
  return scopes !== null;
}

/** The cached row set for a scope, or undefined when the cache is off or cold. */
export function getScope(
  hash: string,
  tableSuffix: string,
  opsMissionType: opsMissionTypeEnum
): parameterStruct[] | undefined {
  return scopes?.get(scopeKey(hash, tableSuffix, opsMissionType));
}

/** Record a scope's complete row set, as the database just returned it. */
export function setScope(
  hash: string,
  tableSuffix: string,
  opsMissionType: opsMissionTypeEnum,
  rows: parameterStruct[],
  token: ReadToken
): void {
  const key = scopeKey(hash, tableSuffix, opsMissionType);
  if (!token.quiet || token.clears !== clears || token.writes !== (writes.get(key) ?? 0)) return;
  scopes?.set(key, rows.slice());
}

/**
 * Follow a write. The stored row set is only usable as an answer to "all rows
 * of this scope", so a write into a scope the cache has not read must be
 * ignored rather than start a partial set — otherwise the next
 * `getParametersArray` would return that one row and call it the whole scope.
 */
export function upsertParam(
  hash: string,
  tableSuffix: string,
  opsMissionType: opsMissionTypeEnum,
  paramTypeId: property,
  row: parameterStruct
): void {
  const key = scopeKey(hash, tableSuffix, opsMissionType);
  writes.set(key, (writes.get(key) ?? 0) + 1);
  const rows = scopes?.get(key);
  if (!rows) {
    return;
  }
  // eslint-disable-next-line eqeqeq
  const at = rows.findIndex((r) => r.parameter == paramTypeId);
  if (at >= 0) {
    rows[at] = row;
  } else {
    rows.push(row);
  }
}

/** Forget every mission's rows for one (hash, tableSuffix) — after a reconcile-to-empty. */
export function dropScope(hash: string, tableSuffix: string): void {
  clears++;
  if (!scopes) {
    return;
  }
  const prefix = hash + "\u0000" + tableSuffix + "\u0000";
  for (const key of Array.from(scopes.keys())) {
    if (key.startsWith(prefix)) {
      scopes.delete(key);
    }
  }
}

/** Forget everything — for a delete whose reach this map cannot express. */
export function clear(): void {
  clears++;
  scopes?.clear();
}
